import * as React from 'react';
import { useTranslation } from 'react-i18next';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import Button from '@mui/material/Button';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Input from '@mui/material/Input';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Divider from '@mui/material/Divider';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { Dayjs } from 'dayjs';
import { Survey, TableSurveysRow } from './TableSurveysRow';

export interface Team {
  id: string;
  name: string;
  leader: string[];
  cohortId?: string;
  surveyIds: string[];
  createdBy?: string;
}

interface TableTeamsRowProps {
  team: Team;
  allSurveys: Survey[];
  assignedSurveyIds: Set<string>;
  onEdit: (team: Team, name: string, leader: string) => Promise<void>;
  onDelete: (teamId: string, deleteSurveys: boolean) => Promise<void>;
  onAddSurvey: (teamId: string, surveyIds: string[]) => Promise<void>;
  onRemoveSurvey: (teamId: string, surveyId: string) => Promise<void>;
  analyzeSurvey: (surveyId: string | number) => void;
  loading: string | number;
  getParticipants: (surveyId: string | number) => void;
  participantsBySurvey: { [key: string]: any };
  setQrModalOpen: (link: string) => void;
  loadingParticipants: string | number;
  isMobile: boolean;
  editLeaderReportDate: (
    survey: Survey,
    leader: string,
    reportDate: Dayjs | null,
    reminderDate: Dayjs | null
  ) => void;
  getLeaderAndReportDate: (surveyId: string | number) => void;
  loadingLeader: string | number;
  leaderBySurvey: { [key: string]: any };
  isAdmin?: boolean;
}

export const TableTeamsRow: React.FC<TableTeamsRowProps> = ({
  team,
  allSurveys,
  assignedSurveyIds,
  onEdit,
  onDelete,
  onAddSurvey,
  onRemoveSurvey,
  analyzeSurvey,
  loading,
  getParticipants,
  participantsBySurvey,
  setQrModalOpen,
  loadingParticipants,
  isMobile,
  editLeaderReportDate,
  getLeaderAndReportDate,
  loadingLeader,
  leaderBySurvey,
  isAdmin,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const [editDialogOpen, setEditDialogOpen] = React.useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [editName, setEditName] = React.useState(team.name);
  const [editLeader, setEditLeader] = React.useState(team.leader.join(', '));
  const [editLoading, setEditLoading] = React.useState(false);
  const [deleteLoading, setDeleteLoading] = React.useState(false);
  const [addSurveyLoading, setAddSurveyLoading] = React.useState(false);
  const [removingId, setRemovingId] = React.useState<string | null>(null);
  const [selectedSurveys, setSelectedSurveys] = React.useState<Survey[]>([]);

  // Surveys that belong to this team
  const teamSurveys = allSurveys.filter((s) => team.surveyIds.includes(String(s.i)));

  // Surveys available to add: not yet assigned to any team
  const availableSurveys = allSurveys
    .filter((s) => !assignedSurveyIds.has(String(s.i)))
    .sort((a, b) => a.t.localeCompare(b.t));

  const handleEdit = async () => {
    setEditLoading(true);
    await onEdit(team, editName, editLeader);
    setEditLoading(false);
    setEditDialogOpen(false);
  };

  const handleDelete = async (deleteSurveys: boolean) => {
    setDeleteLoading(true);
    await onDelete(team.id, deleteSurveys);
    setDeleteLoading(false);
    setDeleteDialogOpen(false);
  };

  const handleAddSurvey = async () => {
    if (selectedSurveys.length === 0) return;
    setAddSurveyLoading(true);
    await onAddSurvey(
      team.id,
      selectedSurveys.map((s) => String(s.i))
    );
    setSelectedSurveys([]);
    setAddSurveyLoading(false);
  };

  const handleRemoveSurvey = async (surveyId: string) => {
    setRemovingId(surveyId);
    await onRemoveSurvey(team.id, surveyId);
    setRemovingId(null);
  };

  return (
    <>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{team.name}</TableCell>
        {!isMobile && <TableCell>{team.leader.join(', ')}</TableCell>}
        {isAdmin && <TableCell>{team.createdBy}</TableCell>}
        <TableCell align="right">
          <IconButton
            size="small"
            onClick={() => {
              setEditName(team.name);
              setEditLeader(team.leader.join(', '));
              setSelectedSurveys([]);
              setEditDialogOpen(true);
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => setDeleteDialogOpen(true)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </TableCell>
      </TableRow>

      {/* Expandable survey list */}
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('teams.surveys')}
              </Typography>
              {teamSurveys.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t('teams.no_surveys')}
                </Typography>
              ) : (
                <Table size="small">
                  <TableBody>
                    {teamSurveys.map((s) => (
                      <TableSurveysRow
                        key={s.i}
                        row={s}
                        analyzeSurvey={analyzeSurvey}
                        loading={loading}
                        getParticipants={getParticipants}
                        participantsBySurvey={participantsBySurvey}
                        setQrModalOpen={setQrModalOpen}
                        loadingParticipants={loadingParticipants}
                        selectedSurveys={[]}
                        handleSelect={() => {}}
                        isMobile={isMobile}
                        editLeaderReportDate={editLeaderReportDate}
                        getLeaderAndReportDate={getLeaderAndReportDate}
                        loadingLeader={loadingLeader}
                        leaderBySurvey={leaderBySurvey}
                        onRemove={
                          removingId === String(s.i)
                            ? undefined
                            : () => handleRemoveSurvey(String(s.i))
                        }
                      />
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      {/* Edit dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
        <DialogTitle>{t('teams.edit')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1, minWidth: 320 }}>
            <Input
              value={editName}
              placeholder={t('teams.name')}
              onChange={(e) => setEditName(e.target.value)}
            />
            <FormControl>
              <Input
                value={editLeader}
                placeholder={t('teams.leader')}
                onChange={(e) => setEditLeader(e.target.value)}
              />
              <FormHelperText>{t('teams.leader_hint')}</FormHelperText>
            </FormControl>
            <Divider />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Autocomplete
                multiple
                size="small"
                options={availableSurveys}
                getOptionLabel={(s) => `${s.t} (${s.i})`}
                value={selectedSurveys}
                onChange={(_, v) => setSelectedSurveys(v)}
                renderInput={(params) => (
                  <TextField {...params} placeholder={t('teams.add_survey')} variant="standard" />
                )}
                sx={{ flex: 1 }}
                isOptionEqualToValue={(a, b) => a.i === b.i}
              />
              <Button
                size="small"
                variant="outlined"
                disabled={selectedSurveys.length === 0 || addSurveyLoading}
                onClick={handleAddSurvey}
              >
                {t('teams.add_survey')}
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleEdit} disabled={editLoading || !editName}>
            {t('common.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('teams.delete_confirm_title')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {t('help.tips.team_delete')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>
            {t('common.cancel')}
          </Button>
          <Button onClick={() => handleDelete(false)} disabled={deleteLoading}>
            {t('teams.delete_keep_surveys')}
          </Button>
          <Button onClick={() => handleDelete(true)} color="error" disabled={deleteLoading}>
            {t('teams.delete_also_surveys')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
